import React from 'react';

interface RatingScaleSelectorProps {
  label?: string;
  description?: string;
  value?: number | null;
  onChange: (value: number) => void;
  disabled?: boolean;
}

interface LevelConfig {
  score: number;
  label: string;
  shortLabel: string;
  subtitle: string;
  activeBg: string;
  activeBorder: string;
  textColor: string;
  emojiIcon: string;
}

const LEVELS: LevelConfig[] = [
  {
    score: 1,
    label: 'Muito Ruim',
    shortLabel: 'Péssimo',
    subtitle: 'Precisamos melhorar muito',
    activeBg: 'bg-rose-500 text-white',
    activeBorder: 'border-rose-500 ring-4 ring-rose-100',
    textColor: 'text-rose-600',
    emojiIcon: '😕',
  },
  {
    score: 2,
    label: 'Abaixo do esperado',
    shortLabel: 'Ruim',
    subtitle: 'Houve falhas ou atrasos',
    activeBg: 'bg-amber-500 text-white',
    activeBorder: 'border-amber-500 ring-4 ring-amber-100',
    textColor: 'text-amber-600',
    emojiIcon: '😐',
  },
  {
    score: 3,
    label: 'Regular / Neutro',
    shortLabel: 'Regular',
    subtitle: 'Atendeu apenas ao básico',
    activeBg: 'bg-yellow-500 text-white',
    activeBorder: 'border-yellow-500 ring-4 ring-yellow-100',
    textColor: 'text-yellow-600',
    emojiIcon: '🙂',
  },
  {
    score: 4,
    label: 'Bom / Satisfeito',
    shortLabel: 'Bom',
    subtitle: 'Experiência positiva e ágil',
    activeBg: 'bg-teal-500 text-white',
    activeBorder: 'border-teal-500 ring-4 ring-teal-100',
    textColor: 'text-teal-600',
    emojiIcon: '😊',
  },
  {
    score: 5,
    label: 'Excelente!',
    shortLabel: 'Excelente',
    subtitle: 'Atendimento impecável e rápido',
    activeBg: 'bg-emerald-600 text-white',
    activeBorder: 'border-emerald-600 ring-4 ring-emerald-100',
    textColor: 'text-emerald-600',
    emojiIcon: '🤩',
  },
];

export const RatingScaleSelector: React.FC<RatingScaleSelectorProps> = ({
  label,
  description,
  value,
  onChange,
  disabled = false,
}) => {
  const currentLevel = LEVELS.find((l) => l.score === value);

  return (
    <div className="w-full">
      {label && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-4">
          <div>
            <h3 className="text-base font-bold text-slate-900 tracking-tight flex items-center gap-1.5">
              {label}
              <span className="text-rose-500 font-bold">*</span>
            </h3>
            {description && (
              <p className="text-xs text-slate-500 mt-0.5">{description}</p>
            )}
          </div>
          {currentLevel && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-50 border border-slate-200 self-start sm:self-center transition-all animate-fadeIn">
              <span className="text-sm">{currentLevel.emojiIcon}</span>
              <span className={`text-xs font-black uppercase tracking-wider ${currentLevel.textColor}`}>
                {currentLevel.label}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Escala Interativa 1 a 5 - Alinhamento Perfeito com texto em todos os números */}
      <div className="grid grid-cols-5 gap-1.5 sm:gap-3">
        {LEVELS.map((level) => {
          const isSelected = value === level.score;

          return (
            <button
              key={level.score}
              type="button"
              disabled={disabled}
              onClick={() => onChange(level.score)}
              className={`group relative flex flex-col items-center justify-between py-2 sm:py-3 px-1 rounded-2xl border transition-all duration-200 cursor-pointer select-none h-20 sm:h-24 ${
                isSelected
                  ? `${level.activeBg} ${level.activeBorder} shadow-md scale-[1.04] ring-2 ring-offset-2 ring-emerald-500`
                  : 'bg-slate-50/80 border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300 active:scale-95 shadow-xs'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {/* Ícone no topo */}
              <span className="text-sm sm:text-base leading-none select-none">
                {level.emojiIcon}
              </span>

              {/* Número centralizado rigorosamente com a mesma altura e baseline para todos */}
              <span className="text-xl sm:text-2xl font-black leading-none my-auto select-none">
                {level.score}
              </span>

              {/* Rótulo de texto presente em TODOS os 5 níveis para manter alinhamento 100% simétrico */}
              <span
                className={`text-[9px] sm:text-xs font-bold leading-none tracking-tight text-center truncate max-w-full select-none ${
                  isSelected
                    ? 'text-white font-extrabold'
                    : 'text-slate-500 group-hover:text-slate-700'
                }`}
              >
                {level.shortLabel}
              </span>
            </button>
          );
        })}
      </div>

      {/* Feedback contextual dinâmico abaixo dos botões */}
      <div className="mt-3.5 min-h-[22px] flex items-center justify-center text-center">
        {currentLevel ? (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100/90 text-slate-700 text-xs font-medium border border-slate-200/80 animate-question-slide">
            <span>{currentLevel.emojiIcon}</span>
            <span className="font-bold text-slate-900">{currentLevel.label}:</span>
            <span className="text-slate-600 italic">"{currentLevel.subtitle}"</span>
          </div>
        ) : (
          <p className="text-xs text-slate-400">
            Toque em uma nota de 1 a 5 para responder
          </p>
        )}
      </div>
    </div>
  );
};

export default RatingScaleSelector;
